import React, { useEffect, useState } from 'react';
import { SignOutButton, useUser } from '@clerk/clerk-react';
import { Link, useNavigate } from 'react-router-dom';
import { colors } from './theme';
import LoadingSpinner from './components/LoadingSpinner';
import NoAccessNotice from './components/NoAccessNotice';

const API_BASE = process.env.REACT_APP_API_URL;

export default function BuildOrdersPage() {
  const { user, isSignedIn } = useUser();
  const role = user?.publicMetadata?.role || 'guest';
  const normalizedRole = String(role).toLowerCase().trim();
  const hasAccess = normalizedRole.startsWith('gpv') || role === 'admin';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [product, setProduct] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [dueDate, setDueDate] = useState('');
  const extractSizeCode = prod => {
    const match = String(prod).toUpperCase().match(/WE(\d{3})/);
    return match ? match[1] : '';
  };
  const [sizeCode, setSizeCode] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = () => {
      setLoading(true);
      fetch(`${API_BASE}/build-orders`)
        .then(res => res.json())
        .then(setOrders)
        .catch(() => {})
        .finally(() => setLoading(false));
    };
    fetchOrders();
    const id = setInterval(fetchOrders, 10000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setSizeCode(extractSizeCode(product));
  }, [product]);

  if (!isSignedIn || !hasAccess) {
    return (
      <div className="ni-page">
        <div className="ni-shell ni-shell--narrow">
          <NoAccessNotice pageName="Build orders" />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ni-page">
        <div className="ni-shell ni-shell--narrow">
          <div className="ni-panel" style={loadingStyle}>
            <LoadingSpinner size={64} />
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async e => {
    e.preventDefault();
    const res = await fetch(`${API_BASE}/build-orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product, quantity: Number(quantity), dueDate }),
    });
    if (res.ok) {
      const newOrder = await res.json();
      setOrders(o => [newOrder, ...o]);
      setShowForm(false);
      setProduct('');
      setQuantity(1);
      setSizeCode('');
      setDueDate('');
    }
  };

  const renderStatus = status => {
    let style;
    if (status === 'In Progress') {
      style = { backgroundColor: '#fff3cd', color: '#856404' };
    } else if (status === 'Completed') {
      style = { backgroundColor: '#d4edda', color: '#155724' };
    } else if (status === 'In Transit') {
      style = { backgroundColor: '#cce5ff', color: '#004085' };
    } else if (status === 'Archived') {
      style = { backgroundColor: '#e2e3e5', color: '#6c757d' };
    } else {
      style = { backgroundColor: '#e2e3e5', color: '#6c757d' };
    }
    return (
      <span style={{ ...style, padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
        {status}
      </span>
    );
  };

  return (
    <div className="ni-page">
      <div className="ni-shell ni-shell--narrow">
        <header className="ni-header">
          <div className="ni-brand">
            <Link to="/" title="Back to main page" aria-label="Back to main page" style={{ display: 'inline-flex', lineHeight: 0 }}>
              <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            </Link>
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">Production</span>
              <span className="ni-brand-subtitle">Build orders and handoff tracking</span>
            </div>
          </div>
          <div className="ni-toolbar">
            <span className="ni-header-meta">View as: GPV production</span>
            <SignOutButton>
              <button className="ni-button ni-button--ghost" style={headerButtonStyle}>Sign out</button>
            </SignOutButton>
          </div>
        </header>

        <div className="ni-panel">
          <div className="ni-section-heading" style={{ marginBottom: '1rem' }}>
            <div>
              <h2 className="ni-section-title">Build orders</h2>
              <p className="ni-section-copy">Track active order queues and create the next production batch.</p>
            </div>
            <div className="ni-toolbar">
              <button
                onClick={() => navigate('/production-status')}
                className="ni-button ni-button--ghost"
              >
                View production status
              </button>
              <button
                onClick={() => setShowForm(true)}
                className="ni-button ni-button--primary"
              >
                Create build order
              </button>
            </div>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="ni-panel ni-panel--dense" style={{ marginBottom: '1rem' }}>
              <div className="ni-grid ni-grid--two">
                <label style={fieldStyle}>
                  <span>Product</span>
                  <input className="ni-input" value={product} onChange={e => setProduct(e.target.value)} />
                </label>
                <label style={fieldStyle}>
                  <span>Quantity</span>
                  <input className="ni-input" type="number" min="1" value={quantity} onChange={e => setQuantity(e.target.value)} />
                </label>
                <label style={fieldStyle}>
                  <span>Size Code</span>
                  <input className="ni-input" value={sizeCode} readOnly />
                </label>
                <label style={fieldStyle}>
                  <span>Due Date</span>
                  <input className="ni-input" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
                </label>
              </div>
              <div className="ni-toolbar" style={{ marginTop: '1rem' }}>
                <button type="submit" className="ni-button ni-button--primary">Create</button>
                <button type="button" onClick={() => setShowForm(false)} className="ni-button ni-button--muted">Cancel</button>
              </div>
            </form>
          )}

          <div className="ni-table-wrap">
            <table className="ni-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Ready By</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr
                    key={o._id}
                    onClick={() => navigate(`/build-order/${o._id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{o.orderNumber}</td>
                    <td>{o.product}</td>
                    <td>{o.quantity}</td>
                    <td>{o.dueDate ? new Date(o.dueDate).toLocaleDateString() : 'N/A'}</td>
                    <td>{renderStatus(o.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const loadingStyle = {
  minHeight: '50vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

const headerButtonStyle = {
  minHeight: '44px'
};

const fieldStyle = {
  display: 'grid',
  gap: '0.45rem',
  color: colors.textBody
};

